// ── participant-scope: the FR-PDASH-001 isolation + FR-REPORT-001 exclusion pins ─
// Wave 9 (Participant Dashboard) — the ISOLATION-CRITICAL capstone.
//
// CONTRACT TRUTH (read from apps/vjal-coaching-api/src): there is NO dedicated
// /participant/* route family. A participant reads the SAME endpoints every role
// reads (GET /cohorts (scope=mine), GET /cohorts/:id/sessions, GET /sessions/:id/
// recordings, GET /cohorts/:id/assignments, GET /auth/me) and the SERVER SCOPE
// RESOLVER pins the results to the participant's OWN data (participantUserId /
// companyId / cohortIds). The server is authoritative — the frontend cannot
// widen the scope. So the participant dashboard is built on those server-scoped
// reads.
//
// This module is the FRONTEND defense-in-depth half of the two release gates:
//   1. FR-PDASH-001 PARTICIPANT ISOLATION — the dashboard renders ONLY the
//      authenticated participant's OWN data. NO peer participant, NO other
//      cohort, NO admin-surface bleed. Beyond the server pin, the participant
//      projection NEVER requests a cross-participant read (no roster, no admin
//      list) and the helpers here re-filter a list to the participant's own
//      cohort set so that even if a (mis-scoped) read returned a peer/other-
//      cohort row, it is dropped before render. A row that is NOT in the
//      participant's own cohort set is treated as out-of-scope and excluded.
//   2. FR-REPORT-001 REPORT EXCLUSION — ZERO reports anywhere on the participant
//      dashboard. The participant projection reads NO report endpoint
//      (PARTICIPANT_DASHBOARD_READS deliberately omits reports.* / analytics
//      report-status / transcript) and the admin modules are route+page denied.
//      `isReportRead()` is the assertion hook the biting tests use to prove the
//      projection never names a report-bearing read.
//
// Fail-closed: an unknown record (no cohort id, or a cohort id not in the
// participant's own set) is OUT of scope. The participant has NO access to the
// admin modules (Programs / Resources / Reports / Companies / Users / Sessions
// admin / Capstone admin / Certificates admin) — `PARTICIPANT_SELF_SERVICE_NAV`
// is the exhaustive own-surface allowlist a participant may ever navigate to.

import type { CoachingCohortDto, CoachingSessionDto } from "../services/coaching-dtos";

// ── The participant's OWN-DATA read set (server-scoped, report-free) ─────────
// The EXACT set of read endpoints the participant dashboard is allowed to fire.
// Each is server-scoped to the participant's own data. The set deliberately
// OMITS every report-bearing read (reports.list, reports.detail, session-report,
// transcript, analytics report-status) and every admin/roster read (participants
// roster, companies, programs, users, history). The biting report-exclusion test
// asserts NONE of these is a report read; the isolation test asserts NONE is a
// cross-participant/admin read.
export const PARTICIPANT_DASHBOARD_READS = [
  "auth.me", // own identity / profile projection
  "cohorts.list", // server-scoped to OWN cohorts (scope=mine)
  "sessions.listForCohort", // OWN cohort's sessions
  "sessions.recordings", // OWN session recordings (presence-only passcode; dark lane → honest)
  "cohorts.assignments", // OWN cohort assignment context (dark lane → honest)
] as const;

export type ParticipantDashboardRead = (typeof PARTICIPANT_DASHBOARD_READS)[number];

// Report-bearing reads a participant must NEVER fire (the exclusion gate). If any
// of these ever appears in PARTICIPANT_DASHBOARD_READS the report-exclusion test
// fails. This is documentation + an assertion target, not a runtime branch.
export const REPORT_BEARING_READS = [
  "reports.list",
  "reports.detail",
  "sessions.report",
  "sessions.transcript",
  "analytics.reportStatus",
] as const;

const reportBearingSet = new Set<string>(REPORT_BEARING_READS);

/** Whether a read key names a report-bearing endpoint (exclusion assertion hook). */
export function isReportRead(readKey: string): boolean {
  if (reportBearingSet.has(readKey)) return true;
  // Belt-and-suspenders: any read whose key mentions report/transcript is
  // report-bearing and forbidden on the participant dashboard.
  return /report|transcript/i.test(readKey);
}

/** Assert (used by the projection + tests) that every participant read is report-free. */
export function participantReadsAreReportFree(): boolean {
  return PARTICIPANT_DASHBOARD_READS.every((read) => !isReportRead(read));
}

// ── The participant's own self-service navigation allowlist (no admin modules) ─
// The EXHAUSTIVE set of route ids a participant may ever reach. Anything not
// here is an admin/operational module the participant is denied (route manifest
// already denies them; this is the assertion source of truth for the nav gate).
export const PARTICIPANT_SELF_SERVICE_NAV = [
  "dashboard", // the Participant Dashboard (their landing + home)
  "my-cohorts", // own cohorts read-only list
  "settings", // own settings shell
  "profile", // own profile (read-only by default — self-edit allowlist empty)
] as const;

export type ParticipantNavId = (typeof PARTICIPANT_SELF_SERVICE_NAV)[number];

const participantNavSet = new Set<string>(PARTICIPANT_SELF_SERVICE_NAV);

/** Whether a route id is an own self-service surface a participant may navigate to. */
export function isParticipantSelfServiceNav(routeId: string): boolean {
  return participantNavSet.has(routeId);
}

// The admin/operational modules a participant must NEVER see in nav or reach by
// route. Used by the isolation tests to prove each one is absent for participant.
export const PARTICIPANT_DENIED_MODULES = [
  "companies",
  "programs",
  "cohorts",
  "participants",
  "sessions",
  "calendar",
  "reports",
  "org-admin-home",
  "org-reports",
  "resources",
  "capstone",
  "certificates",
  "history",
  "users",
  "permissions",
  "exports",
  "settings-thresholds",
  "tools",
] as const;

// ── Own-cohort scope pin (defense-in-depth list filtering) ──────────────────
// The participant's own cohort id set, derived from the server-scoped cohorts
// read (scope=mine). Used to re-filter session lists so a (mis-scoped) peer/
// other-cohort row can never render. The server is authoritative; this is a
// fail-closed client guard.
export function ownCohortIdSet(cohorts: CoachingCohortDto[] | undefined): Set<string> {
  const ids = new Set<string>();
  for (const cohort of cohorts ?? []) {
    if (cohort?.id) ids.add(String(cohort.id));
  }
  return ids;
}

/**
 * Keep only sessions that belong to one of the participant's OWN cohorts. A
 * session with no cohort id, or a cohort id outside the own-set, is OUT of scope
 * and dropped (fail-closed). With a correctly-scoped server read this is a no-op;
 * it BITES only if a cross-cohort row ever leaked in.
 */
export function ownSessionsOnly(
  sessions: CoachingSessionDto[] | undefined,
  ownCohortIds: Set<string>
): CoachingSessionDto[] {
  return (sessions ?? []).filter((session) => {
    const cohortId = session?.cohort_id ? String(session.cohort_id) : "";
    return cohortId.length > 0 && ownCohortIds.has(cohortId);
  });
}

/** Whether a cohort id is one of the participant's own (in-scope) cohorts. */
export function isOwnCohort(cohortId: string | null | undefined, ownCohortIds: Set<string>): boolean {
  if (!cohortId) return false;
  return ownCohortIds.has(String(cohortId));
}
